import React, { useCallback, useEffect, useRef } from 'react';
import TableHeader from '../../BaseComponents/TableHeader';
import './NutritionistTable.scss';
import { useDispatch, useSelector } from 'react-redux';
import {
  appendNutritionistsList,
  NutritionistsPageState
} from '../../../store/nutritionistsSlice';
import NutritionistTableRow from './NutritionistTableRow';
import { getNutritionistsList } from '../../../api/nutritionist';

const headerColumns = [
  { title: 'Avatar', key: 'avatar', minWidth: '64px', flex: 0 },
  { title: 'Nutritionist', key: 'name', minWidth: '170px', flex: 0 },
  { title: 'Calendly url', key: 'calendly', minWidth: '160px', flex: 0 },
  { title: 'Team', key: 'team', minWidth: '140px', flex: 0 },
  { title: 'Bio', key: 'bio', minWidth: '252px', flex: 1 },
  { title: 'Contacts', key: 'contacts', minWidth: '160px', flex: 0 },
  { title: 'Members', key: 'members', minWidth: '80px', flex: 0 },
  { title: '', key: 'actions', minWidth: '48px', flex: 0 }
];

const NutritionistTable: React.FC = () => {
  const [loading, setLoading] = React.useState<boolean>(false);
  const [hasMore, setHasMore] = React.useState<boolean>(true);
  const dispatch = useDispatch();
  const { nutritionistsList, limit } = useSelector(
    (state: { nutritionists: NutritionistsPageState }) => state.nutritionists
  );

  const offsetRef = useRef<number>(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const loadMoreNutritionists = useCallback(async () => {
    if (loading || !hasMore) return;
    
    setLoading(true);
    offsetRef.current += limit;
    try {
      const nutritionistsList = await getNutritionistsList(
        offsetRef.current,
        limit
      );
      if (nutritionistsList.nutritionists.length > 0) {
        dispatch(appendNutritionistsList(nutritionistsList.nutritionists));
        setHasMore(nutritionistsList.nutritionists.length >= limit);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('fetchNutritionists error :>> ', error);
    } finally {
      setLoading(false);
    }
  }, [dispatch, limit, loading, hasMore]);

  const handleScroll = useCallback(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      if (!scrollContainerRef.current || loading || !hasMore) return;
      
      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
      const threshold = 100;
      
      if (scrollTop + clientHeight >= scrollHeight - threshold) {
        loadMoreNutritionists();
      }
    }, 150);
  }, [loading, hasMore, loadMoreNutritionists]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    }
    
    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [handleScroll]);

  const nutritionists = nutritionistsList;

  return (
    <div className='scroll-container'>
      <div className='table-container nutritionist-table'>
        <TableHeader headerColumns={headerColumns} gap={'28px'} />
        <div className='table-body' ref={scrollContainerRef}>
          {nutritionists?.map((nutritionist) => {
            return (
              <NutritionistTableRow
                nutritionistData={nutritionist}
                key={nutritionist.id}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default NutritionistTable;
